<template>
  <!-- Use mouseup.stop to prevent SideLeft/deselectAllSlides function listens to MouseEvent('mouseup'). -->
  <div
    class="aha-right-nav"
    @mouseup.stop
    :class="{
      'powerpoint-addin-sticky': isEnableAddinPowerpoint && rightNavActiveTab
    }"
    v-prevent-wheel-propagation="{ throttleTime: WHEEL_THROTTLE_TIME }"
  >
    <div
      v-for="tab in rightNavAllTabs"
      :key="tab.name"
    >
      <div
        class="right-nav__button"
        :class="{'active': rightNavActiveTab === tab.name,
                 'is-tab-ai-active': hasClassAIActive(tab.name),
                 'disabled': isTabDisabled(tab.name),
        }"
        name="right_nav_button"
        v-aha-emit-action="{ user_input: tab.name }"
        @click.stop="activeTab(tab.name)"
      >
        <aha-icon
          :name="tab.icon"
          :width="tab.iconWidth"
          :height="tab.iconHeight"
          :viewBox="tab.iconViewBox"
          class="right-nav__button-icon"
        ></aha-icon>
        <p class="right-nav__button-label" :lang="getPresenterLocale">
          {{ $i18nPresenter.t(tab.name) }}
        </p>
      </div>

      <div
        v-if="isRightNavDividerVisible(tab.name)"
        class="right-nav__divider-wrapper"
      >
        <div class="right-nav__divider"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import utils from '@/util'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import { WHEEL_THROTTLE_TIME } from '@/constant/fixedTime'

export default {
  props: {
    oneInSelectedSlidesHasAudio: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      WHEEL_THROTTLE_TIME,
    }
  },

  computed: {
    ...mapGetters('rightNav', ['rightNavTabNames', 'rightNavAllTabs', 'rightNavActiveTab', 'isAudioTabDisabled']),
    ...mapGetters('slideType', ['isSlideTypeNoAudio']),
    ...mapGetters('featureToggles', ['canUseAIImprovingSlideContent']),
    ...mapGetters(['selectedSlides', 'getPresenterLocale']),

    ...mapState({
      slideActive: (state) => state.slideActive,
      selectedSlideIds: (state) => state.selections.selectedSlideIds,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
    }),

    isMultipleSlidesSelected() {
      return this.selectedSlideIds.length > 1
    },

    isSlideNotCreated() {
      return !this.slideActive || (this.slideActive && !this.slideActive.id)
    },

    isTabDisabled() {
      return (tabName) => {
        const tabsRequiringSlideType = [this.rightNavTabNames.AUDIO, this.rightNavTabNames.DESIGN]
        const isTabAudio = tabName === this.rightNavTabNames.AUDIO
        const isTabContent = tabName === this.rightNavTabNames.CONTENT

        const isTabRequiringSlideTypeButHasNoSlideType = tabsRequiringSlideType.includes(tabName) &&
          !this.slideActive.type
        const isTabAudioDisabledWhenMultipleSlidesSelected = isTabAudio &&
          this.isMultipleSlidesSelected && this.isAudioTabDisabled
        const isTabAudioDisabledForSingleSlideWithNoAudio = isTabAudio &&
          !this.isMultipleSlidesSelected && this.isSlideTypeNoAudio
        const isTabContentWhenMultipleSlidesSelected = isTabContent && this.isMultipleSlidesSelected

        return isTabRequiringSlideTypeButHasNoSlideType ||
              isTabAudioDisabledWhenMultipleSlidesSelected ||
              isTabAudioDisabledForSingleSlideWithNoAudio ||
              isTabContentWhenMultipleSlidesSelected
      }
    },

    hasClassAIActive() {
      const aiTabs = [this.rightNavTabNames.AI]
      return (tabName) => this.rightNavActiveTab === tabName && aiTabs.includes(tabName)
    },

    isRightNavDividerVisible() {
      const tabsWithDivider = [this.rightNavTabNames.AI, this.rightNavTabNames.AUDIO]
      return (tabName) => tabsWithDivider.includes(tabName)
    },

  },
  watch: {
    oneInSelectedSlidesHasAudio(value) {
      this.setAudioTabDisabled(!value)
    },
  },

  mounted() {
    this.initWheelScrollAction(this.$el)
  },

  methods: {
    ...mapActions('rightNav', ['setRightNavActiveTabAction', 'setAudioTabDisabled']),
    ...mapActions('wheelEvent', ['initWheelScrollAction']),

    activeTab(tabName) {
      if (this.isTabDisabled(tabName)) return

      this.setRightNavActiveTabAction(tabName)

      if (tabName === this.rightNavTabNames.AI) {
        utils.trackGA4AndMixpanel('Open AI Panel', {
          eventAction: 'Open AI Panel',
          eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
          slideId: this.slideActive && this.slideActive.id,
          presentationId: this.presentation.id,
          userId: this.currentUser.id,
          touch_point: 'Right nav',
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.aha-right-nav {
  @apply bg-base-0 h-full pt-4 overflow-y-auto text-secondary-100 border border-base-30;
  box-shadow: 0px 10px 38px 0px #525F7F14;
  width: 98px;
  min-width: 98px;
  z-index: 1;
  .right-nav__button {
    @apply bg-base-0;
    @apply rounded-lg;
    @apply font-bold text-center p-2 mt-0 mx-1 mb-2 cursor-pointer;
    width: 88px;
    transition: all 0.1s ease-in-out;
    &:hover:not(.disabled) {
      @apply hover:bg-secondary-15;
      &.active {
        @apply bg-primary-100;
      }
    }
    &.active:not(.disabled) {
      @apply bg-primary-90 text-base-0;
      .right-nav__button-icon {
        @apply text-base-0;
      }
    }
    &.is-tab-ai-active {
      background: url(~@/assets/images/ai-generator/rightNav-AI-button-background.png);
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
      @apply bg-base-0;
    }
    &.disabled {
      @apply cursor-not-allowed;
    }
  }
  .right-nav__button-icon {
    @apply mb-1 text-secondary-100;
  }
  .right-nav__button-label {
    @apply w-full break-words;
    hyphens: auto;
    word-break: break-word;
  }
  .right-nav__divider-wrapper {
    @apply w-full my-3;
    .right-nav__divider {
      @apply bg-secondary-20 h-0.25 my-0 mx-auto;
      width: 40px;
    }
  }
  &.powerpoint-addin-sticky {
    position: fixed;
    right: 0;
    z-index: 2;
    overflow-x: hidden;
  }
}
</style>
